import React, { ReactNode, useState } from 'react'
import Highlight, { defaultProps, Language } from 'prism-react-renderer'
import CopyToClipboard from 'react-copy-to-clipboard'
import styled from 'styled-components'
import { giphyGreen, giphyLightCharcoal } from '../styles/colors'

const Wrapper = styled.div`
    position: relative;
`

const Pre = styled.pre`
    text-align: left;
    margin: 1em 0;
    padding: 0.5em;

    & .token-line {
        line-height: 1.8em;
        height: 1.8em;

        // There seems to be a bug where an empty line is rendered after each code snippet.
        &:last-child {
            display: none;
        }
    }
`

const CopyCodeButton = styled.span`
    color: ${props => (props.isCodeCopied ? giphyGreen : giphyLightCharcoal)};
    font-family: interface;
    font-weight: bold;
    font-size: 15px;
    position: absolute;
    right: 10px;
    top: 5px;
    cursor: pointer;
`

const LineNo = styled.span`
    display: inline-block;
    width: 2em;
    user-select: none;
    opacity: 0.3;
`

type Props = {
    children: ReactNode
    className?: string
    language?: Language
    hideLineNumber: boolean
    hideCopyCodeButton: boolean
    showLineNumber: boolean
}

const Code = ({ children, className = '', language, showLineNumber = false, hideCopyCodeButton = false }: Props) => {
    // Keep track of whether the user has clicked the 'copy code' button
    const [isCodeCopied, setCodeCopied] = useState(false)

    // language can be a prop in react, or be taken from the markdown className
    language = language || (className.replace(/language-/, '') as Language)

    // default props include Prism.
    return language ? (
        <Wrapper>
            {!hideCopyCodeButton ? (
                <CopyToClipboard text={children as string}>
                    <CopyCodeButton
                        isCodeCopied={isCodeCopied}
                        onClick={() => {
                            setCodeCopied(true)
                            setTimeout(() => {
                                setCodeCopied(false)
                            }, 10000)
                        }}
                    >
                        {isCodeCopied ? 'Copied!' : 'Copy Code'}
                    </CopyCodeButton>
                </CopyToClipboard>
            ) : (
                ''
            )}
            <Highlight {...defaultProps} theme={undefined} code={children as string} language={language}>
                {({ className, style, tokens, getLineProps, getTokenProps }) => (
                    <Pre className={className} style={style}>
                        {tokens.map((line, i) => (
                            <div key={i} {...getLineProps({ line, key: i })}>
                                {showLineNumber ? <LineNo>{i + 1}</LineNo> : ''}
                                {line.map((token, key) => (
                                    <span key={key} {...getTokenProps({ token, key })} />
                                ))}
                            </div>
                        ))}
                    </Pre>
                )}
            </Highlight>
        </Wrapper>
    ) : (
        children
    )
}

export default Code
